import { extractHelperAddresses } from "../../utils/helpersUtils";
import { extractProfileFromRawSnapshot } from "../../utils/snapshotUtils";

export interface ILightUser {
  address: string;
  name: string;
  public_key: string;
  safes: string[];
  helperAddresses: string[];
  baseCreationSignature: string;
}

export const LightUserUtils = {
  create({
    address = "",
    name = "",
    public_key = "",
    safes = [],
    helperAddresses = [],
    baseCreationSignature = "",
  }: {
    address: string;
    name: string;
    public_key: string;
    safes: string[];
    helperAddresses: string[];
    baseCreationSignature: string;
  }): ILightUser {
    return {
      address,
      name,
      public_key,
      safes,
      helperAddresses,
      baseCreationSignature,
    };
  },

  empty(): ILightUser {
    return LightUserUtils.create({
      address: "",
      name: "",
      public_key: "",
      safes: [],
      helperAddresses: [],
      baseCreationSignature: "",
    });
  },

  fromMap(map: Record<string, any>): ILightUser {
    if (!map) {
      console.error("Invalid map provided to LightUserUtils.fromMap");
      return LightUserUtils.empty();
    }
    if (!map["address"]) {
      console.error("Map does not contain 'address' field:", map);
      return LightUserUtils.empty();
    }
    if (!map["name"]) {
      console.error("Map does not contain 'name' field:", map);
      return LightUserUtils.empty();
    }
    if (!Array.isArray(map["groups"]) || map["groups"].length === 0) {
      console.error("Map 'groups' field is not an array:", map);
      return LightUserUtils.empty();
    }

    const profile = extractProfileFromRawSnapshot(map);

    if (!profile || !profile["object"]) {
      console.error("No profile object found in the map:", map);
      return LightUserUtils.empty();
    }

    const profileObject = profile["object"];

    const profileSignature = profileObject["SIGNATURE"];
    const baseCreationSignature = profileSignature?.split("-")[0] ?? "";

    let safes: string[] = [];

    if (Array.isArray(profileObject["safes"])) {
      safes = profileObject["safes"]
        .map((safe: Record<string, any>) => safe["safe_address"])
        .filter((address: string) => address && address !== "");
    }

    const helperAddresses: string[] = [];
    const helpersData = extractHelperAddresses(map);
    helperAddresses.push(...helpersData.helperAddresses);

    try {
      return LightUserUtils.create({
        address: map["address"] || "",
        name: map["name"] || "",
        public_key: profileObject["public_key"] || "",
        safes: safes || [],
        helperAddresses: helperAddresses,
        baseCreationSignature: baseCreationSignature,
      });
    } catch (e) {
      console.error("Error creating ILeanSnapshot from map:", e);
      return LightUserUtils.empty();
    }
  },

  fromJson(source: string): ILightUser {
    try {
      return LightUserUtils.fromMap(JSON.parse(source));
    } catch (e) {
      console.error("Error parsing ILeanSnapshot from JSON:", e);
      return LightUserUtils.empty();
    }
  },

  toMap(user: ILightUser): Record<string, any> {
    return {
      address: user.address,
      name: user.name,
      groups: user.public_key,
      assets: user.safes,
    };
  },

  toJson(user: ILightUser): string {
    return JSON.stringify(LightUserUtils.toMap(user));
  },

  copyWith(
    user: ILightUser,
    {
      address,
      name,
      public_key,
      safes,
      helperAddresses,
      baseCreationSignature,
    }: {
      address?: string;
      name?: string;
      public_key?: string;
      safes?: string[];
      helperAddresses?: string[];
      baseCreationSignature?: string;
    }
  ): ILightUser {
    return LightUserUtils.create({
      address: address ?? user.address,
      name: name ?? user.name,
      public_key: public_key ?? user.public_key,
      safes: safes ?? user.safes,
      helperAddresses: helperAddresses ?? user.helperAddresses,
      baseCreationSignature: baseCreationSignature ?? user.baseCreationSignature,
    });
  },
};
